import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useDataCache } from "../context/DataCacheContext";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FileText, Plus, Pencil, Trash2, Loader2, CalendarIcon, Upload, File, X, Eye, EyeOff, Users, Search, Filter, Download, Lock, Globe, RefreshCw, ChevronDown, FileSpreadsheet, AlertTriangle, Archive, CopyPlus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parse } from "date-fns";
import { ro } from "date-fns/locale";
import { API } from "../config/api";
import { normalizeForSearch, extractNumarIntern, getStageColor } from "../utils/helpers";
import { MentiuniSection } from "../components/MentiuniSection";


const ETAPE_PROCESUALE = [
  "Urmărire penală",
  "Cameră preliminară",
  "Fond",
  "Apel",
  "Recurs",
  "Rejudecare fond",
  "Rejudecare apel",
  "Revizuire",
  "Cerere în anulare",
  "Cerere reexaminare",
  "Executare silită",
  "Închis",
  "Altele",
];

const INSTANTE = [
  "Judecătorie",
  "Tribunal",
  "Curte de Apel",
  "Înalta Curte de Casație și Justiție",
  "Parchet",
  "Executor Judecătoresc",
  "Faza Prejudiciară",
  "Altele",
];

const initialFormState = {
  numar_dosar_intern: "",
  numar_dosar: "",
  parola_dosar: "",
  client_ids: [],
  instanta: "",
  instanta_exacta: "",
  etapa_procesuala: "Fond",
  termene_judecata: [],
  avocat_repartizat_id: "",
  avocat_repartizat_ids: [],
  parte_adversa: [],
  mentiuni: "",
  in_monitorizare: false,
};

export default function CasesPage() {
  const { user, getAuthHeader } = useAuth();
  const { fetchClients: fetchCachedClients, fetchTeamMembers: fetchCachedTeamMembers, invalidateCache } = useDataCache();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [courtDateDialogOpen, setCourtDateDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newCourtDate, setNewCourtDate] = useState({ date: null, description: "" });
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterEtapa, setFilterEtapa] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const [caseTasks, setCaseTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [sortField, setSortField] = useState(null); // 'numar_intern' or null
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  const [syncingCourtDates, setSyncingCourtDates] = useState(false);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [newTermeneCount, setNewTermeneCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const isAdmin = user?.role === "admin";
  // Avocații colaboratori și stagiari pot vedea parola dosar și secțiunea portal.just.ro
  const isAvocat = user?.position === "avocat_colaborator" || user?.position === "avocat_stagiar";
  const canViewPortalAndPassword = isAdmin || isAvocat;

  // Function to extract the numeric prefix from numar_dosar_intern (before "/")
  // Function to get client name for sorting
  const getClientName = (caseItem) => {
    if (caseItem.clients && caseItem.clients.length > 0) {
      return caseItem.clients[0].display_name || '';
    }
    return '';
  };

  // Function to toggle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sorted cases - automatic sort by nr. intern (asc), then by client name
  const getSortedCases = () => {
    return [...cases].sort((a, b) => {
      const internA = (a.numar_dosar_intern || "").toLowerCase().trim();
      const internB = (b.numar_dosar_intern || "").toLowerCase().trim();
      
      // Check categories: normal, dash-only, archived
      const isArchivedA = internA.includes("arhivat");
      const isArchivedB = internB.includes("arhivat");
      const isDashA = !isArchivedA && (internA === "-" || internA === "");
      const isDashB = !isArchivedB && (internB === "-" || internB === "");
      
      // Priority: 1. Normal dosare, 2. Dash/empty, 3. Archived
      // Archived goes to the end
      if (isArchivedA && !isArchivedB) return 1;
      if (!isArchivedA && isArchivedB) return -1;
      if (isArchivedA && isArchivedB) {
        // Both archived - sort by client name
        return getClientName(a).localeCompare(getClientName(b), 'ro');
      }
      
      // Dash/empty goes before archived but after normal
      if (isDashA && !isDashB) return 1;
      if (!isDashA && isDashB) return -1;
      if (isDashA && isDashB) {
        // Both dash - sort by client name
        return getClientName(a).localeCompare(getClientName(b), 'ro');
      }
      
      // Both are normal - apply regular sorting
      if (sortField === 'numar_intern') {
        const numA = extractNumarIntern(a.numar_dosar_intern);
        const numB = extractNumarIntern(b.numar_dosar_intern);
        if (numA !== null && numB !== null) {
          return sortDirection === 'asc' ? numA - numB : numB - numA;
        }
        if (numA !== null) return sortDirection === 'asc' ? -1 : 1;
        if (numB !== null) return sortDirection === 'asc' ? 1 : -1;
        return getClientName(a).localeCompare(getClientName(b), 'ro');
      }
      
      // Default automatic sort: nr. intern crescător, apoi alfabetic după client
      const numA = extractNumarIntern(a.numar_dosar_intern);
      const numB = extractNumarIntern(b.numar_dosar_intern);
      
      // Both have nr. intern - sort numerically
      if (numA !== null && numB !== null) {
        return numA - numB;
      }
      
      // Only one has nr. intern - put it first
      if (numA !== null && numB === null) return -1;
      if (numA === null && numB !== null) return 1;
      
      // Neither has nr. intern - sort alphabetically by client name
      return getClientName(a).localeCompare(getClientName(b), 'ro');
    });
  };

  const sortedCases = getSortedCases();

  const fetchData = useCallback(async () => {
    try {
      // Use cached data for clients and team members
      const [casesRes, clientsData, teamData] = await Promise.all([
        axios.get(`${API}/cases`, getAuthHeader()),
        fetchCachedClients(),
        fetchCachedTeamMembers(),
      ]);
      setCases(casesRes.data);
      setClients(clientsData);
      setTeamMembers(teamData);
    } catch (error) {
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, fetchCachedClients, fetchCachedTeamMembers]);

  useEffect(() => {
    fetchData();
  }, []);

  // Normalize Romanian diacritics for search (a=ă=â, s=ș, t=ț, i=î)
  // Filter cases locally
  const filteredCases = useMemo(() => {
    let result = sortedCases;
    
    if (filterEtapa !== "all") {
      result = result.filter(c => c.etapa_procesuala === filterEtapa);
    }
    
    if (searchQuery.trim()) {
      const query = normalizeForSearch(searchQuery);
      result = result.filter(c => 
        normalizeForSearch(c.numar_dosar).includes(query) ||
        normalizeForSearch(c.numar_dosar_intern).includes(query) ||
        normalizeForSearch(c.parti).includes(query) ||
        normalizeForSearch(c.instanta).includes(query) ||
        (c.client_ids || []).some(cid => {
          const client = clients.find(cl => cl.id === cid);
          return client && normalizeForSearch(client.display_name).includes(query);
        })
      );
    }
    
    return result;
  }, [sortedCases, filterEtapa, searchQuery, clients]);

  // Detect grouped cases (same numar_dosar, multiple etape)
  const groupedDosarMap = useMemo(() => {
    const counts = {};
    filteredCases.forEach(c => {
      const key = c.numar_dosar;
      if (key && key !== "-") {
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [filteredCases]);

  // Handle opening case from URL parameter
  useEffect(() => {
    const openCaseId = searchParams.get("open") || searchParams.get("case");
    if (openCaseId && cases.length > 0 && !detailDialogOpen) {
      const caseToOpen = cases.find(c => c.id === openCaseId);
      if (caseToOpen) {
        openDetailDialog(caseToOpen);
        // Clear the URL parameter
        setSearchParams({});
      }
    }
  }, [searchParams, cases, detailDialogOpen]);

  const handleOpenDialog = (caseItem = null) => {
    setClientSearchTerm("");
    if (caseItem) {
      setSelectedCase(caseItem);
      setFormData({
        numar_dosar_intern: caseItem.numar_dosar_intern,
        numar_dosar: caseItem.numar_dosar,
        parola_dosar: caseItem.parola_dosar || "",
        client_ids: caseItem.client_ids || [],
        instanta: caseItem.instanta,
        instanta_exacta: caseItem.instanta_exacta || "",
        etapa_procesuala: caseItem.etapa_procesuala,
        termene_judecata: caseItem.termene_judecata || [],
        avocat_repartizat_id: caseItem.avocat_repartizat_id || "",
        avocat_repartizat_ids: caseItem.avocat_repartizat_ids || [],
        parte_adversa: Array.isArray(caseItem.parte_adversa) ? caseItem.parte_adversa : (caseItem.parte_adversa ? [caseItem.parte_adversa] : []),
        mentiuni: caseItem.mentiuni || "",
        in_monitorizare: caseItem.in_monitorizare || false,
      });
    } else {
      setSelectedCase(null);
      // Auto-sugera următorul număr intern liber
      let nextIntern = "";
      if (cases.length > 0) {
        const existingNums = cases.map(c => {
          const m = (c.numar_dosar_intern || "").match(/^(\d+)/);
          return m ? parseInt(m[1], 10) : 0;
        }).filter(n => n > 0);
        if (existingNums.length > 0) {
          const maxNum = Math.max(...existingNums);
          nextIntern = String(maxNum + 1);
        }
      }
      setFormData({ ...initialFormState, numar_dosar_intern: nextIntern });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCase(null);
    setFormData(initialFormState);
    setShowPassword(false);
    setClientSearchTerm("");
  };

  const handleClientToggle = (clientId) => {
    setFormData(prev => {
      const newClientIds = prev.client_ids.includes(clientId)
        ? prev.client_ids.filter(id => id !== clientId)
        : [...prev.client_ids, clientId];
      return { ...prev, client_ids: newClientIds };
    });
  };

  const handleSubmit = async (e, skipDuplicateCheck = false) => {
    if (e) e.preventDefault();
    if (!formData.numar_dosar_intern || !formData.numar_dosar || !formData.instanta || formData.client_ids.length === 0) {
      toast.error("Completați toate câmpurile obligatorii și selectați cel puțin un client");
      return;
    }

    // Verificare duplicat număr dosar (doar la creare, nu la editare)
    if (!selectedCase && !skipDuplicateCheck) {
      const duplicates = cases.filter(c => 
        c.numar_dosar === formData.numar_dosar || c.numar_dosar_intern === formData.numar_dosar_intern
      );
      if (duplicates.length > 0) {
        const sameEtapa = duplicates.find(c => c.etapa_procesuala === formData.etapa_procesuala);
        if (sameEtapa) {
          toast.error(`Există deja un dosar identic (${sameEtapa.numar_dosar_intern}) cu aceeași etapă procesuală "${sameEtapa.etapa_procesuala}"`);
          return;
        }
        // Etapă diferită — dialog confirmare
        setDuplicateInfo(duplicates[0]);
        setDuplicateConfirmOpen(true);
        return;
      }
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        avocat_repartizat_id: formData.avocat_repartizat_id || null,
      };

      if (selectedCase) {
        await axios.put(`${API}/cases/${selectedCase.id}`, submitData, getAuthHeader());
        toast.success("Dosar actualizat");
        handleCloseDialog();
        fetchData();
      } else {
        // Create new case
        const response = await axios.post(`${API}/cases`, submitData, getAuthHeader());
        const newCaseId = response.data.id;
        toast.success("Dosar creat");
        handleCloseDialog();
        fetchData();
        
        // Auto-sync from portal.just.ro for new cases
        if (formData.numar_dosar) {
          toast.info("Se sincronizează datele de pe portal.just.ro...");
          try {
            const syncResponse = await axios.post(
              `${API}/portal-just/sync-court-dates/${newCaseId}`,
              {},
              getAuthHeader()
            );
            if (syncResponse.data.success) {
              toast.success(
                `Sincronizare completă: ${syncResponse.data.termene_noi_adaugate || 0} termene, ` +
                `${syncResponse.data.solutii_finale?.length || 0} soluții finale`
              );
              // Refresh data to show updated case
              fetchData();
            } else {
              toast.warning("Nu s-au găsit date pe portal.just.ro pentru acest dosar");
            }
          } catch (syncError) {
            console.error("Sync error:", syncError);
            toast.warning("Sincronizare eșuată. Încercați manual din detaliile dosarului.");
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operațiune eșuată");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCase) return;
    
    try {
      await axios.delete(`${API}/cases/${selectedCase.id}`, getAuthHeader());
      toast.success("Dosar șters");
      setDeleteDialogOpen(false);
      setSelectedCase(null);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergerea dosarului");
    }
  };

  const handleArchive = async (caseItem) => {
    try {
      await axios.patch(`${API}/cases/${caseItem.id}/archive`, {}, getAuthHeader());
      toast.success(`Dosar ${caseItem.numar_dosar_intern || caseItem.numar_dosar} arhivat`);
      fetchData();
    } catch (error) {
      toast.error("Eroare la arhivarea dosarului");
    }
  };

  const handleNewEtapa = (caseItem) => {
    // Pre-fill form with existing case data but clear etapa
    const newFormData = {
      ...initialFormState,
      numar_dosar_intern: caseItem.numar_dosar_intern || "",
      numar_dosar: caseItem.numar_dosar || "",
      parola_dosar: caseItem.parola_dosar || "",
      client_ids: (caseItem.clients || []).map(c => c.id),
      instanta: caseItem.instanta || "",
      instanta_exacta: caseItem.instanta_exacta || "",
      avocat_repartizat_id: caseItem.avocat_repartizat_id || "",
      avocat_repartizat_ids: caseItem.avocat_repartizat_ids || [],
      parte_adversa: Array.isArray(caseItem.parte_adversa) ? caseItem.parte_adversa : (caseItem.parte_adversa ? [caseItem.parte_adversa] : []),
      mentiuni: "",
      etapa_procesuala: "",
      in_monitorizare: caseItem.in_monitorizare || false,
    };
    setDetailDialogOpen(false);
    // Use timeout to ensure detail dialog closes and portal async doesn't interfere
    setTimeout(() => {
      setSelectedCase(null);
      setFormData(newFormData);
      setDialogOpen(true);
    }, 100);
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCase) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      await axios.post(
        `${API}/cases/${selectedCase.id}/documents`,
        formDataUpload,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Document încărcat");
      fetchData();
      const response = await axios.get(`${API}/cases/${selectedCase.id}`, getAuthHeader());
      setSelectedCase(response.data);
    } catch (error) {
      toast.error("Eroare la încărcarea documentului");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!selectedCase) return;

    try {
      await axios.delete(`${API}/cases/${selectedCase.id}/documents/${documentId}`, getAuthHeader());
      toast.success("Document șters");
      const response = await axios.get(`${API}/cases/${selectedCase.id}`, getAuthHeader());
      setSelectedCase(response.data);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergerea documentului");
    }
  };

  const handleAddCourtDate = async () => {
    if (!newCourtDate.date || !selectedCase) return;

    try {
      await axios.post(
        `${API}/cases/${selectedCase.id}/court-dates`,
        {
          date: format(newCourtDate.date, "yyyy-MM-dd"),
          description: newCourtDate.description,
        },
        getAuthHeader()
      );
      toast.success("Termen adăugat");
      setNewCourtDate({ date: null, description: "" });
      setCourtDateDialogOpen(false);
      const response = await axios.get(`${API}/cases/${selectedCase.id}`, getAuthHeader());
      setSelectedCase(response.data);
      fetchData();
    } catch (error) {
      toast.error("Eroare la adăugarea termenului");
    }
  };

  const handleDeleteCourtDate = async (courtDateId) => {
    if (!selectedCase) return;

    try {
      await axios.delete(`${API}/cases/${selectedCase.id}/court-dates/${courtDateId}`, getAuthHeader());
      toast.success("Termen șters");
      const response = await axios.get(`${API}/cases/${selectedCase.id}`, getAuthHeader());
      setSelectedCase(response.data);
      fetchData();
    } catch (error) {
      toast.error("Eroare la ștergerea termenului");
    }
  };

  const handleDownloadDocument = async (doc) => {
    setDownloading(doc.id);
    try {
      const response = await axios.get(
        `${API}/cases/${selectedCase.id}/documents/${doc.id}/download`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Document descărcat");
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Nu aveți permisiunea de descărcare. Solicitați aprobare.");
      } else {
        toast.error("Eroare la descărcarea documentului");
      }
    } finally {
      setDownloading(null);
    }
  };

  const handleCreateEvidenta = async (caseItem) => {
    try {
      // First check if evidenta already exists for this case
      const checkResponse = await axios.get(`${API}/evidenta-dosare/by-case/${caseItem.id}`, getAuthHeader());
      if (checkResponse.data) {
        toast.info("Evidență existentă! Se deschide...");
        // Navigate to evidenta page with this case
        window.location.href = `/evidenta-dosare?case=${caseItem.id}`;
        return;
      }
    } catch (checkError) {
      // 404 means no evidenta exists - we can create one
      if (checkError.response?.status !== 404) {
        toast.error("Eroare la verificarea evidenței");
        return;
      }
    }
    
    try {
      // Get client name
      const clientName = caseItem.clients?.length > 0 
        ? caseItem.clients[0].display_name 
        : "";
      
      // Create new evidenta
      await axios.post(`${API}/evidenta-dosare`, {
        case_id: caseItem.id,
        numar_dosar_intern: caseItem.numar_dosar_intern || "",
        numar_dosar: caseItem.numar_dosar || "",
        nume_client: clientName,
        observatii: "",
        tip_dosar: "civil"
      }, getAuthHeader());
      
      toast.success("Evidență creată cu succes!");
      // Navigate to evidenta page
      window.location.href = `/evidenta-dosare?case=${caseItem.id}`;
    } catch (error) {
      console.error("Create evidenta error:", error);
      toast.error(error.response?.data?.detail || "Eroare la crearea evidenței");
    }
  };

  const handleRequestAccess = async (doc) => {
    try {
      await axios.post(
        `${API}/cases/${selectedCase.id}/documents/${doc.id}/request-access`,
        {},
        getAuthHeader()
      );
      toast.success("Cererea a fost trimisă. Veți fi notificat când este procesată.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la trimiterea cererii");
    }
  };

  const handleSyncCourtDates = async (caseId) => {
    setSyncingCourtDates(true);
    try {
      const response = await axios.post(
        `${API}/portal-just/sync-court-dates/${caseId}`,
        {},
        getAuthHeader()
      );
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh case data - preserve portal_instances from current state
        const updatedCase = await axios.get(`${API}/cases/${caseId}`, getAuthHeader());
        setSelectedCase(prev => ({...updatedCase.data, portal_instances: prev?.portal_instances}));
        fetchData();
      } else {
        toast.info(response.data.message || "Nu s-au găsit termene noi");
      }
    } catch (error) {
      toast.error("Eroare la sincronizare. Verificați manual pe portal.just.ro");
    } finally {
      setSyncingCourtDates(false);
    }
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/cases/export/all`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Export_Dosare_${new Date().toISOString().slice(0, 10)}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export realizat cu succes!");
    } catch (error) {
      toast.error("Eroare la exportul dosarelor");
    } finally {
      setExporting(false);
    }
  };

  const handleExportJson = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/cases/export/json`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Dosare_${new Date().toISOString().slice(0, 10)}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export JSON realizat cu succes!");
    } catch (error) {
      toast.error("Eroare la exportul JSON");
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/cases/export/excel`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Export_Dosare_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export Excel realizat cu succes!");
    } catch (error) {
      toast.error("Eroare la exportul Excel");
    } finally {
      setExporting(false);
    }
  };

  const handleExportSingle = async (caseId, numarDosar) => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/export`, {
        ...getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dosar_${numarDosar.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF generat cu succes!");
    } catch (error) {
      toast.error("Eroare la generarea PDF-ului");
    }
  };

  const openDeleteDialog = (caseItem) => {
    setSelectedCase(caseItem);
    setDeleteDialogOpen(true);
  };

  const openDetailDialog = async (caseItem) => {
    setSelectedCase(caseItem);
    setDetailDialogOpen(true);
    
    // Fetch tasks for this case
    setLoadingTasks(true);
    try {
      const allTasks = await axios.get(`${API}/tasks`, getAuthHeader());
      const filtered = allTasks.data.filter(t => t.case_id === caseItem.id);
      setCaseTasks(filtered);
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setCaseTasks([]);
    } finally {
      setLoadingTasks(false);
    }
    
    // Fetch portal instances (căi de atac)
    try {
      const instancesRes = await axios.get(`${API}/portal-just/case-instances/${caseItem.id}`, getAuthHeader());
      if (instancesRes.data.success && instancesRes.data.instances.length > 0) {
        setSelectedCase(prev => {
          if (!prev || prev.id !== caseItem.id) return prev;
          return {...prev, portal_instances: instancesRes.data.instances};
        });
      }
    } catch (error) {
      console.error("Failed to fetch portal instances:", error);
    }
    
    // Auto-sync court dates if case has a selected portal instance or numar_dosar
    const nd = caseItem.numar_dosar || "";
    const isPortalCase = nd && nd !== "-" && !nd.toUpperCase().includes("/P/") && !(caseItem.instanta || "").toLowerCase().includes("parchet");
    if (isPortalCase) {
      setAutoSyncing(true);
      setNewTermeneCount(0);
      try {
        const syncRes = await axios.post(
          `${API}/portal-just/sync-court-dates/${caseItem.id}`,
          {},
          getAuthHeader()
        );
        if (syncRes.data.success) {
          const updatedCase = await axios.get(`${API}/cases/${caseItem.id}`, getAuthHeader());
          setSelectedCase(prev => {
            if (!prev || prev.id !== caseItem.id) return prev;
            return {...updatedCase.data, portal_instances: prev?.portal_instances};
          });
          const newCount = syncRes.data.termene_count || 0;
          setNewTermeneCount(newCount);
          if (newCount > 0) {
            toast.success(`${newCount} termen${newCount > 1 ? "e noi" : " nou"} sincronizat${newCount > 1 ? "e" : ""} de pe portal.just.ro`);
          }
        }
      } catch (error) {
        console.error("Auto-sync failed:", error);
      } finally {
        setAutoSyncing(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă dosarele...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cases-page">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dosare</h1>
          <p className="font-body text-muted-foreground mt-1">
            Gestionează dosarele juridice
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline"
                  disabled={exporting}
                  data-testid="export-dropdown-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Exportă
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportAll} data-testid="export-pdf">
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF (ZIP)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportJson} data-testid="export-json">
                  <FileText className="w-4 h-4 mr-2" />
                  Export JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportExcel} data-testid="export-excel">
                  <FileText className="w-4 h-4 mr-2" />
                  Export Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => handleOpenDialog()} data-testid="add-case-btn">
              <Plus className="w-4 h-4 mr-2" />
              Adaugă Dosar
            </Button>
          </div>
        )}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după număr dosar sau nume client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-cases"
          />
        </div>
        <Select value={filterEtapa} onValueChange={setFilterEtapa}>
          <SelectTrigger className="w-48" data-testid="filter-etapa">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Toate etapele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate etapele</SelectItem>
            {ETAPE_PROCESUALE.map((etapa) => (
              <SelectItem key={etapa} value={etapa}>{etapa}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      </div>{/* End Sticky Header */}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Toate Dosarele ({cases.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cases.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">Nu există dosare încă.</p>
              <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                Adaugă Primul Dosar
              </Button>
            </div>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="font-body font-semibold cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('numar_intern')}
                    >
                      <div className="flex items-center gap-1">
                        Nr. Intern
                        {sortField === 'numar_intern' && (
                          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                        {!sortField && <span className="text-xs text-muted-foreground">⇅</span>}
                      </div>
                    </TableHead>
                    <TableHead className="font-body font-semibold">Nr. Dosar</TableHead>
                    <TableHead className="font-body font-semibold">Clienți</TableHead>
                    <TableHead className="font-body font-semibold">Instanță</TableHead>
                    <TableHead className="font-body font-semibold">Etapă</TableHead>
                    <TableHead className="font-body font-semibold">Avocat</TableHead>
                    <TableHead className="font-body font-semibold text-right">Acțiuni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCases.map((caseItem) => {
                    const isGrouped = groupedDosarMap[caseItem.numar_dosar] > 1;
                    return (
                    <TableRow key={caseItem.id} className={`table-row-hover ${isGrouped ? 'border-l-[3px] border-l-teal-400' : ''}`} style={isGrouped ? {borderLeftWidth: '3px'} : {}} data-testid={`case-row-${caseItem.id}`}>
                      <TableCell className={`font-mono text-sm ${caseItem.in_monitorizare ? 'text-blue-600 font-semibold' : ''}`}>
                        {caseItem.numar_dosar_intern || "-"}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <span>{caseItem.numar_dosar}</span>
                      </TableCell>
                      <TableCell className="font-body">
                        <div className="flex flex-col gap-0.5">
                          {caseItem.clients?.length > 0 ? (
                            caseItem.clients.map((client, idx) => (
                              <span key={client.id} className="text-sm">
                                {client.display_name}
                                {idx < caseItem.clients.length - 1 && ", "}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-body">{caseItem.instanta}</TableCell>
                      <TableCell>
                        <Badge className={`font-body text-xs ${getStageColor(caseItem.etapa_procesuala)}`}>
                          {caseItem.etapa_procesuala}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-body">
                        {(caseItem.avocat_repartizat_names?.length > 0 ? caseItem.avocat_repartizat_names.join(", ") : caseItem.avocat_repartizat_name) || "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDetailDialog(caseItem)}
                            data-testid={`view-case-${caseItem.id}`}
                            title="Vezi detalii"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleExportSingle(caseItem.id, caseItem.numar_dosar)}
                                data-testid={`export-case-${caseItem.id}`}
                                title="Exportă PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleCreateEvidenta(caseItem)}
                                data-testid={`create-evidenta-${caseItem.id}`}
                                title="Creare Evidență Dosar"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <FileSpreadsheet className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenDialog(caseItem)}
                                data-testid={`edit-case-${caseItem.id}`}
                                title="Editează"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-teal-600 hover:text-teal-700"
                                onClick={() => handleNewEtapa(caseItem)}
                                data-testid={`new-etapa-${caseItem.id}`}
                                title="Etapă nouă"
                              >
                                <CopyPlus className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-amber-600 hover:text-amber-700"
                                onClick={() => handleArchive(caseItem)}
                                data-testid={`archive-case-${caseItem.id}`}
                                title="Arhivează"
                              >
                                <Archive className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => openDeleteDialog(caseItem)}
                                data-testid={`delete-case-${caseItem.id}`}
                                title="Șterge"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="case-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedCase ? "Editează Dosar" : "Adaugă Dosar"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="numar_dosar_intern" className="font-body">Număr Dosar Intern *</Label>
                <Input
                  id="numar_dosar_intern"
                  value={formData.numar_dosar_intern}
                  onChange={(e) => setFormData({ ...formData, numar_dosar_intern: e.target.value })}
                  placeholder="INT-2024-001"
                  data-testid="input-numar-intern"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="numar_dosar" className="font-body">Număr Dosar *</Label>
                <Input
                  id="numar_dosar"
                  value={formData.numar_dosar}
                  onChange={(e) => setFormData({ ...formData, numar_dosar: e.target.value })}
                  placeholder="1234/123/2024"
                  data-testid="input-numar-dosar"
                />
                {formData.numar_dosar.trim().startsWith("-") && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md mt-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      Dosarele cu număr începând cu „-" nu vor fi sincronizate automat cu portal.just.ro
                    </p>
                  </div>
                )}
              </div>
              
              {/* Monitorizare checkbox */}
              <div className="flex items-center space-x-2 pt-2">
                <Checkbox
                  id="in_monitorizare"
                  checked={formData.in_monitorizare}
                  onCheckedChange={(checked) => setFormData({ ...formData, in_monitorizare: checked })}
                  data-testid="checkbox-monitorizare"
                />
                <Label htmlFor="in_monitorizare" className="font-body text-sm cursor-pointer">
                  Dosar în monitorizare
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="parola_dosar" className="font-body">Parolă Dosar</Label>
              <div className="relative">
                <Input
                  id="parola_dosar"
                  type={showPassword ? "text" : "password"}
                  value={formData.parola_dosar}
                  onChange={(e) => setFormData({ ...formData, parola_dosar: e.target.value })}
                  placeholder="Parolă opțională"
                  data-testid="input-parola-dosar"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Client Selection */}
            <div className="space-y-2">
              <Label className="font-body">Clienți * (selectați unul sau mai mulți)</Label>
              <Input
                type="text"
                placeholder="Caută client după nume..."
                value={clientSearchTerm}
                onChange={(e) => setClientSearchTerm(e.target.value)}
                className="mb-2"
                data-testid="client-search-input"
              />
              <ScrollArea className="h-32 rounded-md border p-3">
                <div className="space-y-2">
                  {[...clients]
                    .filter((client) => 
                      clientSearchTerm === "" || 
                      normalizeForSearch(client.display_name || "").includes(normalizeForSearch(clientSearchTerm)) ||
                      normalizeForSearch(client.cnp_cui || "").includes(normalizeForSearch(clientSearchTerm))
                    )
                    .sort((a, b) => {
                      // Clienții selectați merg în capul listei
                      const aSelected = formData.client_ids.includes(a.id);
                      const bSelected = formData.client_ids.includes(b.id);
                      if (aSelected && !bSelected) return -1;
                      if (!aSelected && bSelected) return 1;
                      // Apoi sortare alfabetică
                      return (a.display_name || '').localeCompare(b.display_name || '', 'ro');
                    }).map((client) => (
                    <div key={client.id} className={`flex items-center space-x-2 ${formData.client_ids.includes(client.id) ? 'bg-primary/10 rounded px-2 py-1 -mx-2' : ''}`}>
                      <Checkbox
                        id={`client-${client.id}`}
                        checked={formData.client_ids.includes(client.id)}
                        onCheckedChange={() => handleClientToggle(client.id)}
                        data-testid={`client-checkbox-${client.id}`}
                      />
                      <Label htmlFor={`client-${client.id}`} className="font-body text-sm cursor-pointer">
                        {client.display_name} ({client.client_type === "persoana_fizica" ? "PF" : "PJ"})
                      </Label>
                    </div>
                  ))}
                  {clients.length === 0 && (
                    <p className="text-sm text-muted-foreground">Nu există clienți. Adăugați mai întâi un client.</p>
                  )}
                  {clients.length > 0 && clientSearchTerm && ![...clients].some((c) => 
                    normalizeForSearch(c.display_name || "").includes(normalizeForSearch(clientSearchTerm)) ||
                    normalizeForSearch(c.cnp_cui || "").includes(normalizeForSearch(clientSearchTerm))
                  ) && (
                    <p className="text-sm text-muted-foreground">Niciun client găsit pentru "{clientSearchTerm}"</p>
                  )}
                </div>
              </ScrollArea>
              {formData.client_ids.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.client_ids.length} client{formData.client_ids.length > 1 ? 'i' : ''} selectat{formData.client_ids.length > 1 ? 'ți' : ''}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="font-body">Partea Adversă</Label>
              <ScrollArea className="h-32 rounded-md border p-3">
                <div className="space-y-2">
                  {formData.parte_adversa.map((pa, idx) => (
                    <div key={idx} className="flex items-center space-x-2 bg-red-50 rounded px-2 py-1 -mx-2">
                      <Input
                        value={pa}
                        onChange={(e) => {
                          const updated = [...formData.parte_adversa];
                          updated[idx] = e.target.value;
                          setFormData({ ...formData, parte_adversa: updated });
                        }}
                        placeholder="Nume parte adversă"
                        className="flex-1 h-7 text-sm border-red-200 bg-white"
                        data-testid={`input-parte-adversa-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 h-7 w-7 shrink-0"
                        onClick={() => {
                          const updated = formData.parte_adversa.filter((_, i) => i !== idx);
                          setFormData({ ...formData, parte_adversa: updated });
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  {formData.parte_adversa.length === 0 && (
                    <p className="text-sm text-muted-foreground">Nicio parte adversă adăugată.</p>
                  )}
                </div>
              </ScrollArea>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setFormData({ ...formData, parte_adversa: [...formData.parte_adversa, ""] })}
                data-testid="add-parte-adversa-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Adaugă parte adversă
              </Button>
              {formData.parte_adversa.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.parte_adversa.length} parte{formData.parte_adversa.length > 1 ? ' adverse' : ' adversă'} adăugat{formData.parte_adversa.length > 1 ? 'e' : 'ă'}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="instanta" className="font-body">Instanță/Parchet/Executor *</Label>
                <Select
                  value={formData.instanta}
                  onValueChange={(value) => setFormData({ ...formData, instanta: value })}
                >
                  <SelectTrigger data-testid="select-instanta">
                    <SelectValue placeholder="Selectează" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTANTE.map((inst) => (
                      <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="etapa_procesuala" className="font-body">Etapă Procesuală</Label>
                <Select
                  value={formData.etapa_procesuala}
                  onValueChange={(value) => setFormData({ ...formData, etapa_procesuala: value })}
                >
                  <SelectTrigger data-testid="select-etapa">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ETAPE_PROCESUALE.map((etapa) => (
                      <SelectItem key={etapa} value={etapa}>{etapa}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="instanta_exacta" className="font-body">
                Instanță Exactă (ex: Judecătoria Cluj-Napoca, Tribunal Cluj)
              </Label>
              <Input
                id="instanta_exacta"
                value={formData.instanta_exacta || ""}
                onChange={(e) => setFormData({ ...formData, instanta_exacta: e.target.value })}
                placeholder="ex: Judecătoria Cluj-Napoca"
                data-testid="input-instanta-exacta"
              />
              <p className="text-xs text-muted-foreground">
                Se poate completa automat la sincronizare cu portal.just.ro
              </p>
            </div>

            <div className="space-y-2">
              <Label className="font-body">Avocați Repartizați</Label>
              <ScrollArea className="h-32 rounded-md border p-3">
                <div className="space-y-2">
                  {[
                    ...(user?.role === "admin" ? [{ id: user.id, name: `${user.name} (Admin)` }] : []),
                    ...teamMembers.map(m => ({ id: m.id, name: m.name }))
                  ]
                    .sort((a, b) => {
                      const aSelected = formData.avocat_repartizat_ids.includes(a.id);
                      const bSelected = formData.avocat_repartizat_ids.includes(b.id);
                      if (aSelected && !bSelected) return -1;
                      if (!aSelected && bSelected) return 1;
                      return a.name.localeCompare(b.name, 'ro');
                    })
                    .map((member) => (
                    <div key={member.id} className={`flex items-center space-x-2 ${formData.avocat_repartizat_ids.includes(member.id) ? 'bg-primary/10 rounded px-2 py-1 -mx-2' : ''}`}>
                      <Checkbox
                        id={`avocat-${member.id}`}
                        checked={formData.avocat_repartizat_ids.includes(member.id)}
                        onCheckedChange={(checked) => {
                          const ids = checked
                            ? [...formData.avocat_repartizat_ids, member.id]
                            : formData.avocat_repartizat_ids.filter(id => id !== member.id);
                          setFormData({ ...formData, avocat_repartizat_ids: ids, avocat_repartizat_id: ids[0] || "" });
                        }}
                        data-testid={`avocat-checkbox-${member.id}`}
                      />
                      <Label htmlFor={`avocat-${member.id}`} className="font-body text-sm cursor-pointer">
                        {member.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              {formData.avocat_repartizat_ids.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {formData.avocat_repartizat_ids.length} avocat{formData.avocat_repartizat_ids.length > 1 ? 'i' : ''} selectat{formData.avocat_repartizat_ids.length > 1 ? 'ți' : ''}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="mentiuni" className="font-body">Mențiuni/Observații</Label>
              <Textarea
                id="mentiuni"
                value={formData.mentiuni}
                onChange={(e) => setFormData({ ...formData, mentiuni: e.target.value })}
                placeholder="Observații privitoare la dosar..."
                rows={3}
                data-testid="input-mentiuni"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Anulează
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-case">
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : selectedCase ? (
                  "Actualizează"
                ) : (
                  "Creează"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Case Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="case-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="font-heading">
                  {isAdmin ? "Detalii Dosar" : "Vizualizare Dosar"} - {selectedCase?.numar_dosar}
                </DialogTitle>
                {!isAdmin && (
                  <p className="text-sm text-muted-foreground">
                    Vizualizare limitată - doar informații de bază
                  </p>
                )}
              </div>
              {isAdmin && selectedCase && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-teal-600 border-teal-300 hover:bg-teal-50"
                  onClick={() => handleNewEtapa(selectedCase)}
                  data-testid="detail-new-etapa-btn"
                >
                  <CopyPlus className="w-4 h-4 mr-1.5" />
                  Etapă nouă
                </Button>
              )}
            </div>
          </DialogHeader>
          {selectedCase && (
            <div className="space-y-6">
              {/* Basic Info - vizibil pentru toți */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-muted-foreground">Număr Intern</Label>
                  <p className="font-mono font-medium">{selectedCase.numar_dosar_intern}</p>
                </div>
                <div>
                  <Label className="font-body text-muted-foreground">Număr Dosar</Label>
                  <p className="font-mono font-medium">{selectedCase.numar_dosar}</p>
                </div>
                <div>
                  <Label className="font-body text-muted-foreground">Instanță (tip)</Label>
                  <p className="font-body font-medium">{selectedCase.instanta}</p>
                </div>
                <div>
                  <Label className="font-body text-muted-foreground">Instanță Exactă</Label>
                  <p className="font-body font-medium text-primary">
                    {selectedCase.instanta_exacta || <span className="text-muted-foreground italic">Se va prelua automat</span>}
                  </p>
                </div>
                <div>
                  <Label className="font-body text-muted-foreground">Avocat Repartizat</Label>
                  <p className="font-body font-medium">
                    {selectedCase.avocat_repartizat_names?.length > 0 ? selectedCase.avocat_repartizat_names.join(", ") : selectedCase.avocat_repartizat_name || "Nerepartizat"}
                  </p>
                </div>
                {isAdmin && (
                  <div>
                    <Label className="font-body text-muted-foreground">Etapă Procesuală</Label>
                    <Badge className={`mt-1 ${getStageColor(selectedCase.etapa_procesuala)}`}>
                      {selectedCase.etapa_procesuala}
                    </Badge>
                  </div>
                )}
                {canViewPortalAndPassword && selectedCase.parola_dosar && (
                  <div>
                    <Label className="font-body text-muted-foreground">Parolă Dosar</Label>
                    <p className="font-mono">{selectedCase.parola_dosar}</p>
                  </div>
                )}
              </div>

              {/* Clients - vizibil pentru toți */}
              <div>
                <Label className="font-body text-muted-foreground">Clienți ({selectedCase.clients?.length || 0})</Label>
                <div className="mt-2 space-y-2">
                  {selectedCase.clients?.map((client) => (
                    <div key={client.id} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                      <Badge variant="outline" className="text-xs">
                        {client.client_type === "persoana_fizica" ? "PF" : "PJ"}
                      </Badge>
                      <span className="font-body">{client.display_name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Partea Adversă */}
              {selectedCase.parte_adversa?.length > 0 && (
                <div>
                  <Label className="font-body text-muted-foreground">Partea Adversă ({selectedCase.parte_adversa.length})</Label>
                  <div className="mt-1 space-y-1">
                    {(Array.isArray(selectedCase.parte_adversa) ? selectedCase.parte_adversa : [selectedCase.parte_adversa]).map((pa, idx) => (
                      <Badge key={idx} variant="outline" className="mr-1 text-red-600 border-red-200 bg-red-50">
                        {pa}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Next Court Date - vizibil pentru toți */}
              {selectedCase.urmatorul_termen && (
                <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <Label className="font-body text-primary font-semibold">📅 Următorul Termen</Label>
                  <p className="mt-2 text-lg font-bold text-primary">
                    {selectedCase.urmatorul_termen.display_date || selectedCase.urmatorul_termen.date}
                  </p>
                  {selectedCase.urmatorul_termen.tip_solutie && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedCase.urmatorul_termen.tip_solutie}
                    </p>
                  )}
                </div>
              )}

              {/* Portal Just - pentru admin și avocați */}
              {canViewPortalAndPassword && selectedCase.numar_dosar && selectedCase.numar_dosar !== "-" && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="font-body text-blue-700 dark:text-blue-400 font-semibold flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      Portal Just
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-blue-600"
                      onClick={async () => {
                        try {
                          toast.info("Se actualizează lista de pe portal.just.ro...");
                          const response = await axios.get(`${API}/portal-just/case-instances/${selectedCase.id}?refresh=true`, getAuthHeader());
                          if (response.data.success && response.data.instances.length > 0) {
                            setSelectedCase({...selectedCase, portal_instances: response.data.instances});
                            toast.success("Lista actualizată");
                          } else if (response.data.error) {
                            toast.error(response.data.error);
                          } else {
                            toast.info("Nu s-au găsit instanțe pe portal.just.ro");
                          }
                        } catch (e) {
                          console.error(e);
                          toast.error("Eroare la actualizarea listei");
                        }
                      }}
                    >
                      Actualizează Lista
                    </Button>
                  </div>
                  
                  {selectedCase.portal_instances?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedCase.portal_instances.map((inst, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 bg-background/50 rounded-lg border ${
                            selectedCase.selected_portal_instance_url === inst.url 
                              ? 'border-blue-500 ring-2 ring-blue-500/30' 
                              : 'border-blue-200/50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                                  inst.type === 'Fond' ? 'bg-blue-100 text-blue-700' :
                                  inst.type === 'Apel' ? 'bg-purple-100 text-purple-700' :
                                  inst.type === 'Recurs' ? 'bg-red-100 text-red-700' :
                                  inst.type === 'Cameră preliminară' ? 'bg-amber-100 text-amber-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {inst.type}
                                </span>
                                <span className="font-mono text-sm font-medium">{inst.numar_dosar}</span>
                                {inst.instanta && (
                                  <span className="text-xs text-muted-foreground">({inst.instanta})</span>
                                )}
                              </div>
                              {inst.obiect && (
                                <p className="font-body text-xs text-muted-foreground mt-1 line-clamp-2" title={inst.obiect}>
                                  <span className="font-medium">Obiect:</span> {inst.obiect}
                                </p>
                              )}
                              {inst.stadiu_procesual && (
                                <p className="font-body text-xs text-blue-600 mt-0.5">
                                  <span className="font-medium">Stadiu:</span> {inst.stadiu_procesual}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant={selectedCase.selected_portal_instance_url === inst.url ? "default" : "outline"}
                                size="sm"
                                className={`text-xs ${selectedCase.selected_portal_instance_url === inst.url ? 'bg-blue-600' : 'text-blue-600 border-blue-300'}`}
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/cases/${selectedCase.id}`, {
                                      selected_portal_instance_url: inst.url
                                    }, getAuthHeader());
                                    
                                    toast.info("Se sincronizează datele...");
                                    const syncResponse = await axios.post(
                                      `${API}/portal-just/sync-court-dates/${selectedCase.id}`,
                                      {},
                                      getAuthHeader()
                                    );
                                    
                                    if (syncResponse.data.success) {
                                      const caseResponse = await axios.get(`${API}/cases/${selectedCase.id}`, getAuthHeader());
                                      setSelectedCase({...caseResponse.data, portal_instances: selectedCase.portal_instances, selected_portal_instance_url: inst.url});
                                      toast.success(`Sincronizare completă: ${syncResponse.data.message}`);
                                    } else {
                                      setSelectedCase({...selectedCase, selected_portal_instance_url: inst.url});
                                      toast.warning(syncResponse.data.message || "Sincronizare parțială");
                                    }
                                  } catch (e) {
                                    toast.error("Eroare la sincronizare");
                                    console.error(e);
                                  }
                                }}
                                title="Selectează și sincronizează datele din acest dosar"
                              >
                                {selectedCase.selected_portal_instance_url === inst.url ? "✓ Selectat" : "Selectează & Sincronizează"}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:bg-blue-100"
                                onClick={() => window.open(inst.url, '_blank')}
                              >
                                <Globe className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600 border-blue-300 hover:bg-blue-100"
                      onClick={() => window.open(selectedCase.portal_just_url, '_blank')}
                      data-testid="portal-just-link"
                    >
                      <Globe className="w-4 h-4 mr-1" />
                      Deschide Dosar pe Portal
                    </Button>
                  )}
                </div>
              )}

              {/* Final Solutions - vizibil pentru toți */}
              {selectedCase.solutii_finale && (
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <Label className="font-body text-green-700 dark:text-green-400 font-semibold mb-3 block">⚖️ Soluții Finale</Label>
                  <div className="space-y-3">
                    {selectedCase.solutii_finale.split(/(?=\[\d{2}\.\d{2}\.\d{4}\])/).filter(s => s.trim()).map((solution, idx) => {
                      const dateMatch = solution.match(/^\[(\d{2}\.\d{2}\.\d{4})\]\s*/);
                      const date = dateMatch ? dateMatch[1] : '';
                      const restOfSolution = dateMatch ? solution.replace(dateMatch[0], '') : solution;
                      
                      const colonIndex = restOfSolution.indexOf(':');
                      let tipSolutie = '';
                      let descriere = restOfSolution;
                      
                      if (colonIndex > 0 && colonIndex < 80) {
                        tipSolutie = restOfSolution.substring(0, colonIndex).trim();
                        descriere = restOfSolution.substring(colonIndex + 1).trim();
                      }
                      
                      return (
                        <div key={idx} className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                          <p className="font-semibold text-green-800 dark:text-green-300">
                            {date && <span className="font-mono">{date}</span>}
                            {tipSolutie && (
                              <span className="ml-2 text-green-700 dark:text-green-400">
                                {tipSolutie}
                              </span>
                            )}
                          </p>
                          <p className="mt-2 text-sm text-green-900 dark:text-green-200 whitespace-pre-wrap leading-relaxed">
                            {descriere}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Court Dates - vizibil pentru toți */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Label className="font-body text-muted-foreground">
                      Termene Judecată ({selectedCase.termene_judecata?.length || 0})
                    </Label>
                    {autoSyncing && (
                      <span className="flex items-center gap-1 text-xs text-blue-500 animate-pulse">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        sincronizare...
                      </span>
                    )}
                    {!autoSyncing && newTermeneCount > 0 && (
                      <Badge className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0 animate-in fade-in">
                        +{newTermeneCount} nou{newTermeneCount > 1 ? "e" : ""}
                      </Badge>
                    )}
                  </div>
                  {canViewPortalAndPassword && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-blue-600"
                        onClick={() => handleSyncCourtDates(selectedCase.id)}
                        disabled={syncingCourtDates}
                      >
                        {syncingCourtDates ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-1" />
                        )}
                        Sincronizează
                      </Button>
                      {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCourtDateDialogOpen(true)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Adaugă
                      </Button>
                      )}
                    </div>
                  )}
                </div>
                {selectedCase.termene_judecata?.length > 0 ? (
                  <div className="space-y-3">
                    {[...selectedCase.termene_judecata]
                      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
                      .map((term) => {
                      const cleanDescription = term.description ? 
                        term.description
                          .replace(term.tip_solutie || '', '')
                          .replace(/^[:\-\s]+/, '')
                          .trim() 
                        : '';
                      
                      return (
                        <div 
                          key={term.id}
                          className="p-3 bg-muted/50 rounded-lg border hover:border-primary/30 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="font-mono text-sm font-semibold text-primary">
                                {term.display_date || format(new Date(term.date), "dd/MM/yyyy")}
                                {term.ora && (
                                  <span className="ml-2 text-blue-600 font-medium">
                                    ora {term.ora}
                                  </span>
                                )}
                                {term.tip_solutie && (
                                  <span className="ml-2 font-body text-muted-foreground font-normal">
                                    - {term.tip_solutie}
                                  </span>
                                )}
                              </p>
                            </div>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive h-6 w-6"
                                onClick={() => handleDeleteCourtDate(term.id)}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                          {cleanDescription && (
                            <p className="font-body text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap">
                              {cleanDescription}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center py-4 text-sm text-muted-foreground border rounded-md">
                    Nu există termene adăugate.
                  </p>
                )}
              </div>

              {/* === SECȚIUNI DOAR PENTRU ADMIN === */}
              {isAdmin && (
                <>

              {/* Mențiuni Section - Shared Component */}
              <MentiuniSection
                caseData={selectedCase}
                apiPath="/api/cases"
                getAuthHeader={getAuthHeader}
                onUpdate={(updatedMentiuni) => {
                  setSelectedCase({ ...selectedCase, mentiuni: updatedMentiuni });
                  setCases(cases.map(c => c.id === selectedCase.id ? { ...c, mentiuni: updatedMentiuni } : c));
                }}
                isAdmin={isAdmin}
              />

              {/* Documents Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="font-body text-muted-foreground">
                    Documente ({selectedCase.documents?.length || 0})
                  </Label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={handleUploadDocument}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading}
                      data-testid="upload-document-input"
                    />
                    <Button variant="outline" size="sm" disabled={uploading}>
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Încarcă
                    </Button>
                  </div>
                </div>
                {selectedCase.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedCase.documents.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-md"
                        data-testid={`document-${doc.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <File className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="font-body text-sm font-medium">{doc.filename}</p>
                            <p className="font-mono text-xs text-muted-foreground">
                              {(doc.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isAdmin ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDownloadDocument(doc)}
                                disabled={downloading === doc.id}
                                data-testid={`download-document-${doc.id}`}
                              >
                                {downloading === doc.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteDocument(doc.id)}
                                data-testid={`delete-document-${doc.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadDocument(doc)}
                                disabled={downloading === doc.id}
                                data-testid={`download-document-${doc.id}`}
                              >
                                {downloading === doc.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <Download className="w-4 h-4 mr-2" />
                                )}
                                Descarcă
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRequestAccess(doc)}
                                data-testid={`request-access-${doc.id}`}
                              >
                                <Lock className="w-4 h-4 mr-2" />
                                Solicită acces
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-4 text-sm text-muted-foreground border rounded-md">
                    Nu există documente încărcate.
                  </p>
                )}
              </div>

              {/* Închidere secțiuni doar admin */}
              </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Court Date Dialog */}
      <Dialog open={courtDateDialogOpen} onOpenChange={setCourtDateDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="court-date-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Adaugă Termen de Judecată</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-body">Data Termenului *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newCourtDate.date ? format(newCourtDate.date, "dd/MM/yyyy", { locale: ro }) : "Selectează data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newCourtDate.date}
                    onSelect={(date) => setNewCourtDate(prev => ({ ...prev, date }))}
                    locale={ro}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label htmlFor="term-description" className="font-body">Descriere (opțional)</Label>
              <Input
                id="term-description"
                value={newCourtDate.description}
                onChange={(e) => setNewCourtDate(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Primul termen, Administrare probe..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourtDateDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleAddCourtDate} disabled={!newCourtDate.date}>
              Adaugă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-case-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Șterge Dosar</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Ești sigur că vrei să ștergi dosarul {selectedCase?.numar_dosar}? Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-case"
            >
              Șterge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Confirmation Dialog */}
      <AlertDialog open={duplicateConfirmOpen} onOpenChange={setDuplicateConfirmOpen}>
        <AlertDialogContent data-testid="duplicate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Dosar similar existent</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Există deja dosarul <strong>{duplicateInfo?.numar_dosar_intern}</strong> ({duplicateInfo?.numar_dosar}) cu etapa <strong>"{duplicateInfo?.etapa_procesuala}"</strong>.
              <br /><br />
              Dosarul nou va avea etapa <strong>"{formData.etapa_procesuala}"</strong>.
              <br /><br />
              Doriți să continuați adăugarea?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDuplicateConfirmOpen(false); setDuplicateInfo(null); }}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDuplicateConfirmOpen(false);
                setDuplicateInfo(null);
                handleSubmit(null, true);
              }}
              data-testid="confirm-duplicate-case"
            >
              Da, adaugă dosarul
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
